/**
 * Application-wide search API client for the Run view.
 *
 * Adapts `/api/appsearch/search` to the `GlobalSearchClient` seam the SHARED
 * `@twasta/page-runtime/search` panel consumes. The deployed runtime builds
 * the same function against its own `/app/<name>/api/search` prefix, so the
 * two surfaces render one panel over one contract and cannot drift.
 */

import api from './client'
import type {
  GlobalSearchRequest, GlobalSearchResponse,
} from '@twasta/page-runtime/search'

/**
 * Hard ceiling on one search, comfortably above the server's own 20s document
 * budget. A search box must always resolve into either results or a message a
 * reader can act on: axios has no default timeout, so without this a request
 * the server never finishes leaves a spinner turning with nothing to click and
 * no way to tell whether anything is happening.
 */
const TIMEOUT_MS = 30000

export function globalSearch(
  project: string,
  request: GlobalSearchRequest,
  signal?: AbortSignal,
): Promise<GlobalSearchResponse> {
  return api
    .post<GlobalSearchResponse>(
      '/appsearch/search',
      {
        project,
        query: request.query,
        limit: request.limit ?? 25,
        kinds: request.kinds ?? [],
        repo: request.repo ?? '',
      },
      { signal, timeout: TIMEOUT_MS },
    )
    .then((res) => res.data)
    .catch((error: unknown) => {
      // Say what actually happened. "timeout of 30000ms exceeded" is axios
      // talking to a developer, not an answer to the person who typed.
      const code = (error as { code?: string } | null)?.code
      if (code === 'ECONNABORTED' || code === 'ETIMEDOUT') {
        throw new Error(
          'The search took too long to answer. Try a more specific query.',
        )
      }
      throw error
    })
}

export default globalSearch
